import React, {
  createContext,
  useContext,
  useMemo,
} from 'react';
import { Link } from 'react-router-dom';
import cal from '@iatric/fastlane-client-app-library';

const { auth, ContentArea } = cal;

const FEATURE_KEYS = {
  PACKAGE: 'PACKAGE',
  DEPLOYMENT: 'DEPLOYMENT',
};

const SECURITY_KEYS = {
  CREATE: 'CREATE',
  READ: 'READ',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
};

const DEFAULT_ACCESS = {
  authenticatedUser: null,
  hasPackageAccess: false,
  hasDeploymentAccess: false,
  canSelectDraftDeploymentVersion: false,
};

const FastlaneAccessContext = createContext(DEFAULT_ACCESS);

function getWindowConfig() {
  return window.config || {};
}

function resolveFeatureIdentifier(featureKey) {
  const config = getWindowConfig();
  return config?.APPLICATION_FEATURE?.[featureKey] || '';
}

function resolveSecurityIdentifier(securityKey) {
  const config = getWindowConfig();
  return config?.APPLICATION_SECURITY?.[securityKey] || '';
}

export function hasFastlaneFeatureAccess(authenticatedUser, featureKey, securityKey) {
  const config = getWindowConfig();
  const applicationUuid = String(config?.APPLICATION_UUID || '').trim();
  const featureIdentifier = String(resolveFeatureIdentifier(featureKey) || '').trim();
  const securityIdentifier = resolveSecurityIdentifier(securityKey);
  if (!applicationUuid || !featureIdentifier || !securityIdentifier) return false;
  if (typeof auth?.hasAccessToFeature !== 'function') return false;

  return Boolean(
    auth.hasAccessToFeature(
      authenticatedUser,
      applicationUuid,
      featureIdentifier,
      securityIdentifier,
    ),
  );
}

export function buildFastlaneAccess(authenticatedUser) {
  return {
    authenticatedUser: authenticatedUser || null,
    hasPackageAccess: hasFastlaneFeatureAccess(
      authenticatedUser,
      FEATURE_KEYS.PACKAGE,
      SECURITY_KEYS.READ,
    ),
    hasDeploymentAccess: hasFastlaneFeatureAccess(
      authenticatedUser,
      FEATURE_KEYS.DEPLOYMENT,
      SECURITY_KEYS.READ,
    ),
    canSelectDraftDeploymentVersion: (
      hasFastlaneFeatureAccess(authenticatedUser, FEATURE_KEYS.PACKAGE, SECURITY_KEYS.CREATE)
      || hasFastlaneFeatureAccess(authenticatedUser, FEATURE_KEYS.PACKAGE, SECURITY_KEYS.UPDATE)
    ),
  };
}

function filterItemsByAccess(items, access) {
  return (Array.isArray(items) ? items : []).filter((item) => {
    const feature = String(item?.feature || '').trim();
    if (!feature) return true;
    if (typeof auth?.hasAccessToFeature !== 'function') return false;

    const config = getWindowConfig();
    const applicationUuid = String(config?.APPLICATION_UUID || '').trim();
    const readSecurityIdentifier = resolveSecurityIdentifier(SECURITY_KEYS.READ);
    if (!applicationUuid || !readSecurityIdentifier) return false;

    return Boolean(
      auth.hasAccessToFeature(
        access.authenticatedUser,
        applicationUuid,
        feature,
        readSecurityIdentifier,
      ),
    );
  });
}

function AccessDenied({ detail }) {
  return (
    <ContentArea header="Access Denied">
      <div className="card p-4">
        <h5 className="mb-2">You do not have access to this area.</h5>
        <p className="mb-3">{detail}</p>
        <div>
          <Link className="btn btn-primary" to="/deployment/wizard">Open Deployment Wizard</Link>
        </div>
      </div>
    </ContentArea>
  );
}

export function PackageAccessDenied() {
  return (
    <AccessDenied detail="Package access is required to open application and version configuration screens." />
  );
}

export function DeploymentAccessDenied() {
  return (
    <AccessDenied detail="Deployment access is required to open deployment execution and operations screens." />
  );
}

export function hasRouteFeatureAccess(authenticatedUser, featureKey, securityKey = SECURITY_KEYS.READ) {
  return hasFastlaneFeatureAccess(authenticatedUser, featureKey, securityKey);
}

export function filterNavigationByAccess(navigation, authenticatedUser) {
  const access = buildFastlaneAccess(authenticatedUser);
  return {
    ...navigation,
    main: filterItemsByAccess(navigation?.main, access),
    data: filterItemsByAccess(navigation?.data, access),
    advanced: filterItemsByAccess(navigation?.advanced, access),
    report: filterItemsByAccess(navigation?.report, access),
  };
}

export function FastlaneAccessProvider({ authenticatedUser, children }) {
  const value = useMemo(() => buildFastlaneAccess(authenticatedUser), [authenticatedUser]);
  return (
    <FastlaneAccessContext.Provider value={value}>
      {children}
    </FastlaneAccessContext.Provider>
  );
}

export function useFastlaneAccess() {
  return useContext(FastlaneAccessContext);
}
